import React, { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { toDate, toStr } from "./utils";
import { DatePickerProps } from "./types";
import "./daypicker.css";
import { formatDate } from "../../utils/formatDate";

export default function DatePicker({
  label,
  required,
  error,
  value,
  onChange,
  placeholder,
  min,
  max,
  iconSrc,
  className = "",
  placement = "top",
  highlightToday = false,
}: Readonly<DatePickerProps>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const selected = useMemo(() => toDate(value), [value]);
  const minDate = useMemo(() => toDate(min), [min]);
  const maxDate = useMemo(() => toDate(max), [max]);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open && dialogRef.current?.open) {
      dialogRef.current.close();
    }
  }, [open]);

  const popoverPos =
    placement === "top"
      ? "bottom-[calc(100%+8px)] left-0"
      : "top-[calc(100%+8px)] left-0";

  const modifiersClassNames = useMemo(
    () => ({
      selected: "dp-selected",
      ...(highlightToday ? { today: "dp-today" } : {}),
    }),
    [highlightToday]
  );

  return (
    <div className={`block ${className}`} ref={ref}>
      {label && (
        <span className="text-[13px] font-semibold text-[#3B3B3B]">
          {label} {required && <span className="text-[#E54848]">*</span>}
        </span>
      )}

      <div className="relative">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={label ? `${label} calendar` : "calendar"}
          onClick={() => setOpen((o) => !o)}
          className="mt-2 h-[44px] w-full rounded-[6px] border border-[#DFE3EB] bg-[#F1F5F9] px-3 pr-9 text-left text-[14px] text-[#3D3D3D] outline-none hover:border-[#cfd6e5] focus:border-[#1D58E2] focus:ring-2 focus:ring-[#1D58E2]/20"
        >
          {value ? (
            <span className="font-medium">{formatDate(value)}</span>
          ) : (
            <span className="text-[#221F20]/60">{placeholder}</span>
          )}
        </button>

        {iconSrc && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center">
            <img
              src={iconSrc}
              alt="calendar"
              className="h-full w-full object-contain"
            />
          </div>
        )}

        {open && (
          <dialog
            ref={dialogRef}
            open
            onCancel={(e) => {
              e.preventDefault();
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
            aria-label={label ? `${label} calendar` : "calendar"}
            className={`dp-popover absolute z-50 ${popoverPos} m-0 w-[320px] rounded-[12px] border border-[#E6E8EE] bg-white p-2 shadow-[0_8px_24px_rgba(0,0,0,0.12)]`}
          >
            <DayPicker
              mode="single"
              selected={selected}
              onSelect={(d) => {
                onChange(toStr(d));
                setOpen(false);
              }}
              startMonth={minDate}
              endMonth={maxDate}
              weekStartsOn={1}
              defaultMonth={selected ?? new Date()}
              modifiersClassNames={modifiersClassNames}
            />
          </dialog>
        )}
      </div>

      {error && (
        <span className="mt-1 block text-[12px] leading-[14px] text-[#FF0000]">
          {error}
        </span>
      )}
    </div>
  );
}
